import { useState } from 'react';
import { type DataTableProps, DataTableTypes } from './DataTableTypes';

interface Props<
  QUERY_MODEL extends DataTableTypes.QueryModel = DataTableTypes.QueryModel,
  SORT_MODEL extends DataTableTypes.SortModel = DataTableTypes.SortModel
> {
  initialModel?: DataTableTypes.InputTableModel<QUERY_MODEL, SORT_MODEL> | undefined;
  onModelChange: (model: DataTableTypes.TableModel<QUERY_MODEL, SORT_MODEL>) => void;
  totalCount: number;
}

/**
 * Hook to manage / storage DataTable model state and changes.
 * Don't use together with external model control like Quick Views.
 * @param props
 */
export function useDataTableModel<
  QUERY_MODEL extends DataTableTypes.QueryModel = DataTableTypes.QueryModel,
  SORT_MODEL extends DataTableTypes.SortModel = DataTableTypes.SortModel
>({
  initialModel,
  onModelChange,
  totalCount
}: Props<QUERY_MODEL, SORT_MODEL>): Pick<
  DataTableProps<any, QUERY_MODEL, SORT_MODEL>,
  'model' | 'onModelChange' | 'requestOnInit'
> {
  const [model, setModel] = useState<DataTableTypes.InputTableModel<QUERY_MODEL, SORT_MODEL>>(() =>
    Object.assign({}, DEFAULT_TABLE_MODEL, initialModel)
  );
  return {
    model: {
      ...model,
      page: {
        ...model.page,
        totalCount: totalCount
      }
    },
    onModelChange: (model) => {
      setModel(model);
      onModelChange(model);
    },
    requestOnInit: true
  };
}

const DEFAULT_TABLE_MODEL: DataTableTypes.InputTableModel<any, any> = {
  page: {
    limit: 30,
    offset: 0,
    totalCount: 0
  },
  sort: undefined,
  query: {}
};
